/**
 * A2uiDriftPanel — renders one blueprint BOTH ways (ViewIR `view_ir:1` and the
 * a2ui `view_ir:2` conversion) side by side, and lists where the rendered output
 * drifts. This is the studio's converter-fidelity check: the a2ui path renders by
 * reconstructing the equivalent blueprint (the inverse of the converter), so any
 * drift is a converter bug, surfaced here as differing text lines + an element
 * count delta rather than requiring a device to notice.
 *
 * Self-contained (no coupling to the preview pane's state) so it can be mounted
 * behind a toggle without touching the live-preview path.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import NativeWidgetRuntime from '../NativeWidgetRuntime';
import { convertBlueprint } from '../a2ui/convert';

/** Ordered visible leaf-text of a container (same rule the parity test uses). */
function leafTexts(root) {
  if (!root) return [];
  return Array.from(root.querySelectorAll('*'))
    .filter((el) => el.children.length === 0 && el.textContent && el.textContent.trim())
    .map((el) => el.textContent.trim());
}

function diffTexts(a, b) {
  const out = [];
  const max = Math.max(a.length, b.length);
  for (let i = 0; i < max; i += 1) {
    if (a[i] !== b[i]) out.push({ i, viewir: a[i] ?? '∅', a2ui: b[i] ?? '∅' });
  }
  return out;
}

export default function A2uiDriftPanel({ blueprint, feed, pageId, pageArgs = null, appearance = 'light', host = null }) {
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const [drift, setDrift] = useState(null);

  const a2uiView = useMemo(() => {
    try { return convertBlueprint(blueprint); } catch (e) { return { _error: e.message }; }
  }, [blueprint]);

  useEffect(() => {
    // measure after paint
    const id = requestAnimationFrame(() => {
      const l = leafTexts(leftRef.current);
      const r = leafTexts(rightRef.current);
      const elL = leftRef.current ? leftRef.current.querySelectorAll('*').length : 0;
      const elR = rightRef.current ? rightRef.current.querySelectorAll('*').length : 0;
      setDrift({ textDiffs: diffTexts(l, r), elementDelta: elR - elL, count: l.length });
    });
    return () => cancelAnimationFrame(id);
  }, [blueprint, pageId, pageArgs, appearance]);

  if (a2uiView && a2uiView._error) {
    return (
      <div data-testid="a2ui-drift" style={{ padding: 12, color: '#b00', fontSize: 13 }}>
        Conversion failed: {a2uiView._error}
      </div>
    );
  }

  const frame = { flex: 1, minWidth: 0, border: '1px solid #e5e5ea', borderRadius: 12, overflow: 'hidden', height: 520 };
  const noDrift = drift && drift.textDiffs.length === 0 && drift.elementDelta === 0;

  return (
    <div data-testid="a2ui-drift" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>ViewIR (view_ir:1)</div>
          <div ref={leftRef} style={frame}>
            <NativeWidgetRuntime widgetId={blueprint?.widget_id} blueprint={blueprint} feed={feed} pageId={pageId} pageArgs={pageArgs} appearance={appearance} dataMode="mock" host={host} />
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>a2ui (converted, view_ir:2)</div>
          <div ref={rightRef} style={frame}>
            <NativeWidgetRuntime widgetId={blueprint?.widget_id} blueprint={a2uiView} feed={feed} pageId={pageId} pageArgs={pageArgs} appearance={appearance} dataMode="mock" host={host} />
          </div>
        </div>
      </div>
      <div data-testid="a2ui-drift-report" style={{ fontSize: 12, border: '1px solid #e5e5ea', borderRadius: 10, padding: 10 }}>
        {!drift ? 'measuring…' : noDrift ? (
          <span style={{ color: '#2E9B57' }}>✓ No render drift — {drift.count} text nodes match, element counts equal.</span>
        ) : (
          <div>
            <div style={{ color: '#b00', fontWeight: 600, marginBottom: 6 }}>
              Drift: {drift.textDiffs.length} text diff(s), element Δ {drift.elementDelta}
            </div>
            <ul style={{ margin: 0, paddingLeft: 16 }}>
              {drift.textDiffs.slice(0, 20).map((d) => (
                <li key={d.i}><code>#{d.i}</code> viewir=<b>{d.viewir}</b> · a2ui=<b>{d.a2ui}</b></li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
